import _ from "lodash";
import moment from "moment";
import * as turf from "@turf/turf";
import { CHART_SERIES_KEY } from "@/views/enterprise/addon/fuel-consumption/Constants.js";
import { toPlusMinusSignString } from "@/utils/NumberUtil";

const swapPoint = coordinates => {
  if (!coordinates || coordinates.length < 2) return [];
  return [coordinates[1], coordinates[0]];
};

const secToTime = seconds => {
  let accumulatedSeconds = { hour: "-", min: "-" };
  if (seconds) {
    const hour = Math.floor(seconds / 3600);
    const min = Math.floor((seconds % 3600) / 60);
    let hh;
    if (hour < 100) {
      hh = `00${hour}`.slice(-2);
    } else {
      hh = hour;
    }
    const mm = `00${min}`.slice(-2);
    accumulatedSeconds = { hour: hh, min: mm };
  }
  return accumulatedSeconds;
};

const parseComma = (value, round) => {
  if (typeof value !== "number") return "-";
  if (value) {
    return parseFloat(value.toFixed(round)).toLocaleString();
  } else {
    return "-";
  }
};

const createTableDate = route => {
  const averageSpeedThroughWater = route.average_speed_through_water
    ? route.average_speed_through_water
    : "-";
  const mainEngineFuelConsumptionPerNm =
    route.main_engine_fuel_consumption && route.accumulated_distance
      ? route.main_engine_fuel_consumption / route.accumulated_distance
      : "-";
  const mainEngineFuelConsumptionPerHour =
    route.main_engine_fuel_consumption && route.accumulated_seconds
      ? route.main_engine_fuel_consumption /
        (route.accumulated_seconds / 60 / 60)
      : "-";

  return {
    accumulatedDistance: parseComma(route.accumulated_distance, 0),
    accumulatedSeconds: secToTime(route.accumulated_seconds, 0),
    averageSpeedThroughWater: parseComma(averageSpeedThroughWater, 2),
    mainEngineFuelConsumption: parseComma(
      route.main_engine_fuel_consumption,
      1
    ),
    mainEngineFuelConsumptionPerNm: parseComma(
      mainEngineFuelConsumptionPerNm,
      1
    ),
    mainEngineFuelConsumptionPerHour: parseComma(
      mainEngineFuelConsumptionPerHour,
      1
    ),
  };
};

const createDiffTableDate = route => {
  const tableDate = createTableDate(route);
  if (route.main_engine_fuel_consumption > 0) {
    tableDate.mainEngineFuelConsumption =
      tableDate.mainEngineFuelConsumption !== "-"
        ? `+${tableDate.mainEngineFuelConsumption}`
        : "-";
    tableDate.mainEngineFuelConsumptionPerNm =
      tableDate.mainEngineFuelConsumptionPerNm !== "-"
        ? `+${tableDate.mainEngineFuelConsumptionPerNm}`
        : "-";
    tableDate.mainEngineFuelConsumptionPerHour =
      tableDate.mainEngineFuelConsumptionPerHour !== "-"
        ? `+${tableDate.mainEngineFuelConsumptionPerHour}`
        : "-";
  }
  return tableDate;
};

export default {
  /*
  船舶一覧
  */
  getVessels: state => {
    return state.vessels;
  },

  getVessel: state => id => {
    return state.vessels.find(item => item.id === id);
  },

  /*
    航海一覧
  */
  getVoyages: state => {
    return state.voyages;
  },

  getCurrentVoyageIndex: state => id => {
    return state.voyages.findIndex(item => {
      return item.voyage_history_id === id;
    });
  },

  getIsFirstIndex: state => id => {
    const currentIndex = state.voyages.findIndex(item => {
      return item.voyage_history_id === id;
    });
    return currentIndex === state.voyages.length - 1;
  },

  getIsLastIndex: state => id => {
    const currentIndex = state.voyages.findIndex(item => {
      return item.voyage_history_id === id;
    });
    return currentIndex === 0;
  },

  //次の航路を取得する
  getNextVoyageId: state => currentId => {
    const currentIndex = state.voyages.findIndex(item => {
      return item.voyage_history_id === currentId;
    });
    return state.voyages[currentIndex - 1].voyage_history_id;
  },

  //前の航路を取得する
  getPrevVoyageId: state => currentId => {
    const currentIndex = state.voyages.findIndex(item => {
      return item.voyage_history_id === currentId;
    });
    return state.voyages[currentIndex + 1].voyage_history_id;
  },

  /*
    省エネ解析
  */

  /*
    港
  */

  getStartPort: state => {
    return state.startPort;
  },

  getStartPoint: state => {
    if (!state.startPort) return [];
    return swapPoint(state.startPort.coordinates);
  },

  getGoalPort: state => {
    return state.goalPort;
  },

  getGoalPoint: state => {
    if (!state.goalPort) return [];
    return swapPoint(state.goalPort.coordinates);
  },

  getStartPortDate: (_, getters) => {
    if (!getters.getActualRoute || !getters.getActualRoute.start_time)
      return "-";
    return moment(getters.getActualRoute.start_time).format("YYYY-MM-DD HH:mm");
  },

  getGoalPortDate: (_, getters) => {
    if (!getters.getActualRoute || !getters.getActualRoute.goal_time)
      return "-";
    return moment(getters.getActualRoute.goal_time).format("YYYY-MM-DD HH:mm");
  },

  /*
    航路
  */

  getUsualRoute: state => {
    return state.usualRoute;
  },

  getRecomendedRoute: state => {
    return state.recomendedRoute;
  },

  getActualRoute: state => {
    const obj = {};
    Object.keys(state.actualRoute).forEach(key => {
      if (state.actualRoute[key] === "NaN") {
        obj[key] = null;
      } else {
        obj[key] = state.actualRoute[key];
      }
    });
    return obj;
  },

  getActualAccumlatedDistance: state => {
    if (!state.actualRoute) return "-";
    return state.actualRoute.accumulated_distance.toFixed(2);
  },

  getUsualRouteTableData: state => {
    return createTableDate(state.usualRoute);
  },

  getRecomendedRouteTableData: state => {
    return createTableDate(state.recomendedRoute);
  },

  getActualRouteTableData: state => {
    return createTableDate(state.actualRoute);
  },

  getUsualRoutePeriodTableData: state => {
    let acr = state.actualRoute;
    let ur = state.usualRoute;
    let ace = state.actualRouteExistsRecommendedAggregatedData;

    const accumulatedDistanceDiff = parseComma(
      acr.accumulated_distance - ur.accumulated_distance,
      0
    );

    const accumulatedSecondsDiff = secToTime(
      acr.accumulated_seconds - ur.accumulated_seconds,
      0
    );

    const averageSpeedThroughWater = parseComma(
      ur.average_speed_through_water,
      2
    );

    const mainEngineFuelConsumptionDiff = parseComma(
      ace.main_engine_fuel_consumption - ur.main_engine_fuel_consumption,
      1
    );

    let ma = ace.main_engine_fuel_consumption / ace.accumulated_distance;
    let mb = ur.main_engine_fuel_consumption / ur.accumulated_distance;
    const mainEngineFuelConsumptionPerNm = parseComma(ma - mb, 1);

    let ra =
      ace.main_engine_fuel_consumption / (ace.accumulated_seconds / 3600);
    let rb = ur.main_engine_fuel_consumption / (ur.accumulated_seconds / 3600);
    const mainEngineFuelConsumptionPerHour = parseComma(ra - rb, 1);

    return {
      accumulatedDistance: accumulatedDistanceDiff,
      accumulatedSeconds: accumulatedSecondsDiff,
      averageSpeedThroughWater: averageSpeedThroughWater,
      mainEngineFuelConsumption: toPlusMinusSignString(
        mainEngineFuelConsumptionDiff
      ),
      mainEngineFuelConsumptionPerNm: toPlusMinusSignString(
        mainEngineFuelConsumptionPerNm
      ),
      mainEngineFuelConsumptionPerHour: toPlusMinusSignString(
        mainEngineFuelConsumptionPerHour
      ),
    };
  },

  getRecomendedRoutePeriodTableData: state => {
    let acr = state.actualRoute;
    let rr = state.recomendedRoute;
    let ace = state.actualRouteExistsRecommendedAggregatedData;

    let accumulatedDistanceDiff = "-";
    if (rr.accumulated_distance) {
      accumulatedDistanceDiff = parseComma(
        acr.accumulated_distance - rr.accumulated_distance,
        0
      );
    }

    let accumulatedSecondsDiff = "-";
    if (rr.accumulated_seconds) {
      accumulatedSecondsDiff = secToTime(
        acr.accumulated_seconds - rr.accumulated_seconds,
        0
      );
    }

    let averageSpeedThroughWater = "-";
    if (rr.average_speed_through_water) {
      averageSpeedThroughWater = parseComma(rr.average_speed_through_water, 2);
    }

    let mainEngineFuelConsumptionDiff = "-";
    if (rr.main_engine_fuel_consumption) {
      mainEngineFuelConsumptionDiff = toPlusMinusSignString(
        parseComma(
          ace.main_engine_fuel_consumption - rr.main_engine_fuel_consumption,
          1
        )
      );
    }

    let mainEngineFuelConsumptionPerNm = "-";
    if (rr.main_engine_fuel_consumption && rr.accumulated_distance) {
      let ma = ace.main_engine_fuel_consumption / ace.accumulated_distance;
      let mb = rr.main_engine_fuel_consumption / rr.accumulated_distance;
      mainEngineFuelConsumptionPerNm = toPlusMinusSignString(
        parseComma(ma - mb, 1)
      );
    }

    let mainEngineFuelConsumptionPerHour = "-";
    if (rr.main_engine_fuel_consumption && rr.accumulated_seconds) {
      let ra =
        ace.main_engine_fuel_consumption / (ace.accumulated_seconds / 3600);
      let rb =
        rr.main_engine_fuel_consumption / (rr.accumulated_seconds / 3600);
      mainEngineFuelConsumptionPerHour = toPlusMinusSignString(
        parseComma(ra - rb, 1)
      );
    }

    return {
      accumulatedDistance: accumulatedDistanceDiff,
      accumulatedSeconds: accumulatedSecondsDiff,
      averageSpeedThroughWater: averageSpeedThroughWater,
      mainEngineFuelConsumption: mainEngineFuelConsumptionDiff,
      mainEngineFuelConsumptionPerNm: mainEngineFuelConsumptionPerNm,
      mainEngineFuelConsumptionPerHour: mainEngineFuelConsumptionPerHour,
    };
  },

  getActualRoutePeriodTableData: state => {
    return createTableDate(state.actualRoute);
  },

  getExistsRecommendedAggregatedData: state => {
    return createTableDate(state.actualRouteExistsRecommendedAggregatedData);
  },

  getRouteAgreementRate: state => {
    return state.routeAgreementRate;
  },

  getOperationRate: state => {
    return state.operationRate * 100;
  },

  /*
    マップ
  */

  getUsualRouteWaypoints: state => {
    if (!state.usualRouteWaypoints || state.usualRouteWaypoints.length === 0)
      return [];
    return state.usualRouteWaypoints.map(item => {
      const coordinates = swapPoint(item);
      coordinates.push(item[2]);
      return coordinates;
    });
  },

  getRecomendedRouteWaypoints: state => {
    if (
      !state.recomendedRouteWaypoints ||
      state.recomendedRouteWaypoints.length === 0
    )
      return [];
    return state.recomendedRouteWaypoints.map(item => {
      const coordinates = swapPoint(item);
      coordinates.push(item[2]);
      return coordinates;
    });
  },

  getActualRouteWaypoints: state => {
    if (!state.actualRouteWaypoints || state.actualRouteWaypoints.length === 0)
      return [];
    return state.actualRouteWaypoints.map(item => {
      const coordinates = swapPoint(item);
      coordinates.push(item[2]);
      return coordinates;
    });
  },

  getActualRoutesWaypoints: state => {
    if (!state.actualRouteWaypoints || state.actualRouteWaypoints.length === 0)
      return [];
    return state.actualRouteWaypoints.map(routes => {
      return routes.map(route => {
        const coordinates = swapPoint(route);
        coordinates.push(route[2]);
        coordinates.push(route[3]);
        return coordinates;
      });
    });
  },

  getMapCenterPosition: state => {
    return state.mapCenterPosition;
  },

  /*
    グラフ
  */

  //dataに値を入れた状態でSeriesを取得する
  getChartSeries: (state, getters) => {
    const series = _.cloneDeep(state.chartSeries).map(item => {
      if (!state.chartData || state.chartData.length === 0) return item;

      //グラフに表示しないデータは取得しない
      if (!item.showGraphData) return item;

      const chartData = getters.getChartData(item.key);
      item.data = chartData;
      return item;
    });
    return series;
  },

  getFilteredShowSwitchChartSeries: state => {
    return state.chartSeries.filter(item => {
      return item.showSwitch;
    });
  },

  getAllChartData: state => {
    return state.chartData.map(item => {
      const totalFuelConsumption =
        item.main_engine_fuel_consumption +
        item.generator_fuel_consumption +
        item.boiler_fuel_consumption;
      if (item.accumulated_distance && totalFuelConsumption > 0) {
        item[CHART_SERIES_KEY.ENGINE_FUEL_CONSUMPUTION_PER_MILE] =
          totalFuelConsumption / item.accumulated_distance;
      } else {
        item[CHART_SERIES_KEY.ENGINE_FUEL_CONSUMPUTION_PER_MILE] = null;
      }
      item[CHART_SERIES_KEY.OPERATION_RATE] = 0;
      if (item.accumulated_seconds !== 0) {
        let s = moment(item.goal_time).unix() - moment(item.start_time).unix();
        item[CHART_SERIES_KEY.OPERATION_RATE] = item.accumulated_seconds / s;
      }
      return item;
    });
  },

  getChartData: state => key => {
    const data = state.chartData.map(item => {
      if (item[key] === 0) return 0.0;
      if (!item[key] || isNaN(item[key])) return null;
      return Math.round(item[key] * 100) / 100;
    });
    return data;
  },

  getWindSpeedData: (_, getters) => {
    return getters.getChartData(CHART_SERIES_KEY.WIND_SPEED);
  },

  getWindDirectionData: (_, getters) => {
    return getters.getChartData(CHART_SERIES_KEY.WIND_DIRECTION);
  },

  getEnabledSwitchModels: state => {
    return state.enabledSwitchModels;
  },

  getVoyageTimeRange: state => {
    return {
      start_at: state.actualRoute.start_time,
      end_at: state.actualRoute.goal_time,
    };
  },

  getActualRouteLinesFeatures: state => {
    return state.actualRouteLinesFeatures;
  },

  getGraphSyncLine: state => index => {
    return state.sectionLinesFeatures[index];
  },

  getRouteFeatureCollection: state => {
    return state.routeFeatureCollection;
  },

  getMapInitialPosition: state => {
    return state.mapInitialPosition;
  },
};
